/**
 * Garage tab (`/garage`).
 *
 * Lists all cars the user can access — owned + shared. Each row links to
 * `/garage/:id` for details. An "Add car" button opens AddCarModal.
 *
 * Empty state directs the user to add their first car.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import CarIcon from "../../components/CarIcon";
import Icon from "../../components/Icon";
import { useCars, useSetActiveCar } from "../../api/cars";
import { useActiveCar } from "../../auth/useActiveCar";
import type { CarSummary } from "../../api/types";
import AddCarModal from "./AddCarModal";

export default function GaragePage() {
  const navigate = useNavigate();
  const cars = useCars();
  const { activeCar } = useActiveCar();
  const setActive = useSetActiveCar();
  const [isAddOpen, setIsAddOpen] = useState(false);

  if (cars.isLoading) {
    return (
      <section className="page-section">
        <p className="empty-state">Loading…</p>
      </section>
    );
  }

  if (cars.isError) {
    return (
      <section className="page-section">
        <p className="empty-state">
          Could not load your garage:{" "}
          {cars.error instanceof Error ? cars.error.message : "unknown error"}.
        </p>
      </section>
    );
  }

  const list = cars.data?.cars ?? [];

  return (
    <>
      <section className="page-section">
        <div className="car-detail-nav">
          <button
            type="button"
            className="button button--ghost button--small"
            onClick={() => navigate("/account")}
          >
            ← Back to Account
          </button>
        </div>
        <header className="section-header section-header--with-actions">
          <h1 className="section-header__title">Garage</h1>
          <div className="section-header__actions">
            <button
              type="button"
              className="button button--ghost car-detail-action"
              aria-label="Add car"
              title="Add car"
              onClick={() => setIsAddOpen(true)}
            >
              <Icon name="plus" />
            </button>
          </div>
        </header>

        {list.length === 0 ? (
          <div className="empty-state">
            <p>You don't have any cars yet.</p>
            <button
              type="button"
              className="button button--primary"
              onClick={() => setIsAddOpen(true)}
            >
              Add your first car
            </button>
          </div>
        ) : (
          <ul className="garage-list">
            {list.map((car) => (
              <CarRow
                key={car.id}
                car={car}
                isActive={car.id === activeCar?.id}
                setActive={setActive}
              />
            ))}
          </ul>
        )}
      </section>

      <AddCarModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} />
    </>
  );
}

function CarRow({
  car,
  isActive,
  setActive,
}: {
  car: CarSummary;
  isActive: boolean;
  setActive: ReturnType<typeof useSetActiveCar>;
}) {
  const subtitle = [car.manufacturer, car.model].filter(Boolean).join(" · ");
  const isActivating = setActive.isPending && setActive.variables === car.id;

  return (
    <li className="garage-row">
      <Link to={`/garage/${car.id}`} className="garage-row__link">
        <span className="garage-row__icon">
          {car.has_image ? (
            <img
              src={`/api/cars/${car.id}/image`}
              alt=""
              className="garage-row__image"
            />
          ) : (
            <CarIcon name={car.icon} size={36} />
          )}
        </span>
        <span className="garage-row__body">
          <span className="garage-row__title">
            <span className="garage-row__name">{car.name}</span>
            {car.access_level !== "owner" && (
              <span className="garage-row__shared">
                Shared by {car.owner_username}
              </span>
            )}
          </span>
          {subtitle ? (
            <span className="garage-row__sub">{subtitle}</span>
          ) : (
            <span className="garage-row__sub garage-row__sub--muted">
              No make/model
            </span>
          )}
        </span>
      </Link>
      <span className="garage-row__action">
        {isActive ? (
          <span
            className="garage-row__active"
            aria-label={`${car.name} is the active car`}
            title="Active car"
          >
            <Icon name="check" />
          </span>
        ) : (
          <button
            type="button"
            className="garage-row__activate"
            aria-label={`Set ${car.name} as active car`}
            title="Set as active car"
            disabled={setActive.isPending}
            onClick={() => setActive.mutate(car.id)}
          >
            {isActivating && <span className="sr-only">Activating…</span>}
          </button>
        )}
      </span>
    </li>
  );
}
